import React, { useEffect } from 'react';
import { 
  Box, Button, Card, CardContent, Chip, Grid, 
  IconButton, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Typography 
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon 
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAutomations } from '../../hooks/useAutomations';
import { useAutomationStats } from '../../hooks/useAutomationStats';
import useEmailsSent from '../../hooks/useEmailsSent';
import formatSchedule from '../../utils/formatSchedule';
import { runFormatTests } from '../../test_format_time';
import { useAppTheme } from '../../contexts/ThemeContext';
import DevOnly from '../../components/DevOnly';

const AutomationsList = () => {
  const { automations, isLoading, error, toggleAutomation, deleteAutomation, cloneAutomation } = useAutomations();
  const { stats, isLoading: statsLoading, error: statsError } = useAutomationStats();
  const { emailsSent, isLoading: emailsLoading, error: emailsError } = useEmailsSent();
  const { currentTheme = {} } = useAppTheme() || {};

  // Run our test function on component load
  useEffect(() => {
    console.group('🔍 DEBUGGING SCHEDULE TIME ISSUE');
    console.log('Running formatSchedule tests with known times...');
    
    // Test with a few known schedule types and times
    const testSchedules = [
      {
        title: "First Automation",
        scheduleType: "DAILY",
        scheduleTime: "12:07:00",
        scheduleDay: null,
        scheduleDays: null
      },
      {
        title: "Botox 90 Days Re-Engagement Campaign",
        scheduleType: "DAILY",
        scheduleTime: "09:00:00",
        scheduleDay: null,
        scheduleDays: null
      }
    ];
    
    testSchedules.forEach(schedule => {
      console.log(`\nAutomation: ${schedule.title}`);
      console.log(`Input time: ${schedule.scheduleTime}`);
      // For test schedules, no conversion needed (already in local time) 
      console.log(`Formatted: ${formatSchedule(schedule, false)}`);
    });
    
    console.log('\nDirect tests with known time values:');
    const testTimes = [
      { scheduleType: 'DAILY', scheduleTime: '10:00:00' },
      { scheduleType: 'DAILY', scheduleTime: '08:00:00' },
      { scheduleType: 'DAILY', scheduleTime: '12:07:00' }
    ];
    
    testTimes.forEach(schedule => {
      // For test times, no conversion needed (already in local time)
      console.log(`Time ${schedule.scheduleTime} → ${formatSchedule(schedule, false)}`);
    });
  }, []);

  const activeAutomations = automations.filter(automation => automation.status !== 'DELETED');

  if (isLoading || statsLoading || emailsLoading) return <Typography>Loading automations...</Typography>;
  // Do not block render on errors; show page with safe fallbacks
  const hasErrors = Boolean(error || statsError || emailsError);

  // Helper function to transform automation data for formatSchedule
  const getFormattedSchedule = (automation) => {
    if (!automation) return 'N/A';
    
    // Critical debugging
    console.group(`🔴 CRITICAL SCHEDULE DEBUG for "${automation.title}" (${automation.id})`);
    console.log('Raw automation data:', automation);
    
    // Log the exact paths through the data object to diagnose issues
    console.log('Direct property access:');
    console.log('- automation.scheduleTime =', automation.scheduleTime);
    console.log('- type:', typeof automation.scheduleTime);
    
    // Create schedule object that will be directly passed to formatSchedule
    const schedule = {
      scheduleType: automation.scheduleType,
      scheduleInterval: automation.scheduleInterval,
      scheduleTime: automation.scheduleTime,
      scheduleDays: automation.scheduleDays,
      scheduleDay: automation.scheduleDay
    };
    
    console.log('Final schedule object being sent to formatSchedule:', schedule);
    
    // IMPORTANT: The time values in 'schedule' are ALREADY converted from UTC to local time by useAutomations.js
    // We must pass isUTC=false to prevent double conversion
    const formattedSchedule = formatSchedule(schedule, false);
    console.log(`Final formatted schedule: "${formattedSchedule}"`);
    console.groupEnd();
    
    return formattedSchedule;
  };

  return (
    <Box>
      {/* Automations Header */}
      {currentTheme?.isGradient ? (
        <Box className="page-header-gradient" sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Marketing Automations
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
            Create and manage automated marketing campaigns
          </Typography>
          <DevOnly>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              component={Link} 
              to="/automations/create"
            >
              Create Automation
            </Button>
          </DevOnly>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Marketing Automations</Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            component={Link} 
            to="/automations/create"
          >
            Create Automation
          </Button>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total Automations</Typography>
              <Typography variant="h3">{activeAutomations.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Active</Typography>
              <Typography variant="h3">
                {stats?.active ?? activeAutomations.filter(a => a.status === 'ACTIVE').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Emails Sent (Last 30 Days)</Typography>
              <Typography variant="h3">{emailsSent ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Schedule Description</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Recipients (Latest Run)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeAutomations.map((automation) => (
              <TableRow key={automation.id}>
                <TableCell component="th" scope="row">
                  <Link to={`/automations/${automation.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography>{automation.title}</Typography>
                    <Typography variant="body2" color="textSecondary">{automation.emailSubject}</Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={automation.status} 
                    color={automation.status === 'ACTIVE' ? 'success' : 'default'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>{getFormattedSchedule(automation)}</TableCell>
                <TableCell>
                  {automation.lastRun 
                    ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            bgcolor: automation.lastExecutionStatus === 'COMPLETED' ? 'success.main' : 
                                    automation.lastExecutionStatus === 'FAILED' ? 'error.main' : 'warning.main'
                          }} 
                        />
                        {new Date(automation.lastRun).toLocaleString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false
                        })}
                      </Box>
                    ) 
                    : 'Never'}
                </TableCell>
                <TableCell>{automation.recipientCount || 0}</TableCell>
                <TableCell align="right">
                  <DevOnly>
                    <IconButton 
                      color={automation.status === 'ACTIVE' ? 'warning' : 'success'}
                      onClick={() => toggleAutomation(automation.id)}
                      title={automation.status === 'ACTIVE' ? 'Pause' : 'Activate'}
                    >
                      {automation.status === 'ACTIVE' ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                  </DevOnly>
                  <DevOnly>
                    <IconButton 
                      color="primary" 
                      component={Link} 
                      to={`/automations/${automation.id}/edit`}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                  </DevOnly>
                  <DevOnly>
                    <IconButton 
                      color="info" 
                      onClick={() => cloneAutomation(automation.id)}
                      title="Clone"
                    >
                      <CloneIcon />
                    </IconButton>
                  </DevOnly>
                  <DevOnly>
                    <IconButton 
                      color="error" 
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this automation?')) {
                          deleteAutomation(automation.id);
                        }
                      }}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </DevOnly>
                </TableCell>
              </TableRow>
            ))}
            {activeAutomations.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ py: 4 }}>
                    No automations yet. <Link to="/automations/create">Create your first automation</Link>
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {hasErrors && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
          Displaying partial data due to temporary fetch issues.
        </Typography>
      )}
      </Box>
  );
};

export default AutomationsList;